// src/components/charts/TimeSeriesChart.jsx
import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush
} from 'recharts';
import { Select } from '../ui/select';
import { Card, CardContent } from '../ui/card';

const TimeSeriesChart = ({ 
  data, 
  metrics = ['temperature'], 
  colors = ['#ff7300'],
  units,
  setUnits,
  convertValue,
  height = 400
}) => {
  const formatXAxis = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  };

  const getUnitSymbol = (metric) => {
    const unitMap = {
      temperature: units.temperature === 'C' ? '°C' : '°F',
      humidity: '%',
      pressure: units.pressure === 'hPa' ? 'hPa' : 'inHg',
      dewPoint: units.temperature === 'C' ? '°C' : '°F',
      heatIndex: units.temperature === 'C' ? '°C' : '°F',
      wetBulb: units.temperature === 'C' ? '°C' : '°F',
      absoluteHumidity: 'g/m³',
      airDensity: 'kg/m³'
    };
    return unitMap[metric] || '';
  };

  const getMetricUnit = (metric) => {
    if (['temperature', 'dewPoint', 'heatIndex', 'wetBulb'].includes(metric)) {
      return 'temperature';
    }
    if (metric === 'pressure') {
      return 'pressure';
    }
    return null;
  };

  // Convert value based on selected units
  const convertMetricValue = (value, metric) => {
    const unitType = getMetricUnit(metric);
    if (!unitType) return value;
    return convertValue(value, unitType);
  };

  const formatTooltip = (value, name) => {
    const convertedValue = convertMetricValue(value, name);
    const roundedValue = Number(convertedValue).toFixed(2);
    return [`${roundedValue} ${getUnitSymbol(name)}`, name];
  };

  const formatYAxis = (value, metric) => {
    const convertedValue = convertMetricValue(value, metric);
    return Number(convertedValue).toFixed(2);
  };

  // Calculate domain margins for each metric with unit conversion
  const getDomain = (metric) => {
    const values = data.map(d => convertMetricValue(d[metric], metric));
    const min = Math.min(...values);
    const max = Math.max(...values);
    const margin = (max - min) * 0.1;
    return [min - margin, max + margin];
  };

  return (
    <div className="space-y-4">
      {/* Unit Controls */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <label htmlFor="tempUnit" className="text-sm font-medium">
                Temperature:
              </label>
              <Select
                id="tempUnit"
                value={units.temperature}
                onChange={(e) => setUnits(prev => ({ ...prev, temperature: e.target.value }))}
                className="w-24"
              >
                <option value="C">°C</option>
                <option value="F">°F</option>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <label htmlFor="pressureUnit" className="text-sm font-medium">
                Pressure:
              </label>
              <Select
                id="pressureUnit"
                value={units.pressure}
                onChange={(e) => setUnits(prev => ({ ...prev, pressure: e.target.value }))}
                className="w-24"
              >
                <option value="hPa">hPa</option>
                <option value="inHg">inHg</option>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={height}>
        <LineChart 
          data={data} 
          margin={{ top: 20, right: 60, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="timestamp" 
            tickFormatter={formatXAxis}
            angle={-45}
            textAnchor="end"
            height={50}
            tick={{ fontSize: 12 }}
            interval="preserveStartEnd"
          />
          <YAxis 
            yAxisId="left"
            domain={getDomain(metrics[0])}
            tickFormatter={(value) => formatYAxis(value, metrics[0])}
            tick={{ fontSize: 12 }}
            width={60}
            label={{ 
              value: getUnitSymbol(metrics[0]), 
              angle: -90, 
              position: 'insideLeft',
              offset: 0
            }}
          />
          {metrics.length > 1 && (
            <YAxis 
              yAxisId="right" 
              orientation="right"
              domain={getDomain(metrics[1])}
              tickFormatter={(value) => formatYAxis(value, metrics[1])}
              tick={{ fontSize: 12 }}
              width={60}
              label={{ 
                value: getUnitSymbol(metrics[1]), 
                angle: 90, 
                position: 'insideRight',
                offset: 0
              }}
            />
          )}
          <Tooltip 
            formatter={formatTooltip}
            labelFormatter={(label) => new Date(label).toLocaleString()}
            contentStyle={{ fontSize: '12px' }}
          />
          <Legend 
            verticalAlign="top" 
            height={36}
            wrapperStyle={{ fontSize: '12px' }}
          />
          <Brush 
            dataKey="timestamp" 
            height={30} 
            stroke="#8884d8"
            tickFormatter={formatXAxis}
          />
          
          {metrics.map((metric, index) => (
            <Line
              key={metric}
              type="monotone"
              dataKey={metric}
              stroke={colors[index % colors.length]}
              yAxisId={index % 2 === 0 ? "left" : "right"}
              dot={false}
              name={`${metric} (${getUnitSymbol(metric)})`}
              strokeWidth={2}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TimeSeriesChart;